import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["orientation", "rtl", "virtualFocus", "focusLoop", "focusWrap", "focusShift"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useMemo, useCallback } from 'react';
import { reverseArray, flatten2DArray } from 'ariakit-utils/array';
import { useControlledState, useInitialValue, useLiveRef } from 'ariakit-utils/hooks';
import { useStorePublisher } from 'ariakit-utils/store';
import { useCollectionState } from '../collection/collection-state.js';
import { b as getActiveId, a as findFirstEnabledItem, c as getItemsInRow, d as getEnabledItems, e as flipItems, v as verticalizeItems, n as normalizeRows, g as groupItemsByRows, h as getOppositeOrientation } from '../__utils-7009759c.js';
/**
 * Provides state for the `Composite` component.
 * @example
 * ```jsx
 * const composite = useCompositeState();
 * <Composite state={composite}>
 *   <CompositeItem>Item 1</CompositeItem>
 *   <CompositeItem>Item 2</CompositeItem>
 *   <CompositeItem>Item 3</CompositeItem>
 * </Composite>
 * ```
 */

function useCompositeState(_temp) {
  var _props$includesBaseEl;

  var _ref = _temp === void 0 ? {} : _temp,
      _ref$orientation = _ref.orientation,
      orientation = _ref$orientation === void 0 ? "both" : _ref$orientation,
      _ref$rtl = _ref.rtl,
      rtl = _ref$rtl === void 0 ? false : _ref$rtl,
      _ref$virtualFocus = _ref.virtualFocus,
      virtualFocus = _ref$virtualFocus === void 0 ? false : _ref$virtualFocus,
      _ref$focusLoop = _ref.focusLoop,
      focusLoop = _ref$focusLoop === void 0 ? false : _ref$focusLoop,
      _ref$focusWrap = _ref.focusWrap,
      focusWrap = _ref$focusWrap === void 0 ? false : _ref$focusWrap,
      _ref$focusShift = _ref.focusShift,
      focusShift = _ref$focusShift === void 0 ? false : _ref$focusShift,
      props = _objectWithoutProperties(_ref, _excluded);

  var collection = useCollectionState(props);
  var baseRef = useRef(null);

  var _useControlledState = useControlledState(0, props.moves, props.setMoves),
      _useControlledState2 = _slicedToArray(_useControlledState, 2),
      moves = _useControlledState2[0],
      setMoves = _useControlledState2[1];

  var _useControlledState3 = useControlledState(props.defaultActiveId, props.activeId, props.setActiveId),
      _useControlledState4 = _slicedToArray(_useControlledState3, 2),
      _activeId = _useControlledState4[0],
      setActiveId = _useControlledState4[1];

  var activeId = useMemo(function () {
    return getActiveId(collection.items, _activeId);
  }, [collection.items, _activeId]);
  var initialActiveId = useInitialValue(activeId);
  var includesBaseElement = (_props$includesBaseEl = props.includesBaseElement) != null ? _props$includesBaseEl : initialActiveId === null;
  var activeIdRef = useLiveRef(activeId);
  var move = useCallback(function (id) {
    // move() does nothing
    if (id === undefined) return;
    setMoves(function (prevMoves) {
      return prevMoves + 1;
    });
    setActiveId(id);
  }, []);
  var first = useCallback(function () {
    var firstItem = findFirstEnabledItem(collection.items);
    return firstItem == null ? void 0 : firstItem.id;
  }, [collection.items]);
  var last = useCallback(function () {
    var firstItem = findFirstEnabledItem(reverseArray(collection.items));
    return firstItem == null ? void 0 : firstItem.id;
  }, [collection.items]);
  var getNextId = useCallback(function (items, orientation, hasNullItem, skip) {
    // RTL doesn't make sense on vertical navigation
    var isHorizontal = orientation !== "vertical";
    var isRTL = rtl && isHorizontal;
    var allItems = isRTL ? reverseArray(items) : items; // If there's no item focused, we just move the first one.

    if (activeIdRef.current == null) {
      var _findFirstEnabledItem;

      return (_findFirstEnabledItem = findFirstEnabledItem(allItems)) == null ? void 0 : _findFirstEnabledItem.id;
    }

    var activeItem = allItems.find(function (item) {
      return item.id === activeIdRef.current;
    }); // If there's no item focused, we just move to the first one.

    if (!activeItem) {
      var _findFirstEnabledItem2;

      return (_findFirstEnabledItem2 = findFirstEnabledItem(allItems)) == null ? void 0 : _findFirstEnabledItem2.id;
    }

    var isGrid = !!activeItem.rowId;
    var activeIndex = allItems.indexOf(activeItem);
    var nextItems = allItems.slice(activeIndex + 1);
    var nextItemsInRow = getItemsInRow(nextItems, activeItem.rowId); // Home, End, PageUp, PageDown

    if (skip !== undefined) {
      var nextEnabledItemsInRow = getEnabledItems(nextItemsInRow, activeIdRef.current);

      var _nextItem = nextEnabledItemsInRow.slice(skip)[0] || // If we can't find an item, just return the last one.
      nextEnabledItemsInRow[nextEnabledItemsInRow.length - 1];

      return _nextItem == null ? void 0 : _nextItem.id;
    }

    var oppositeOrientation = getOppositeOrientation( // If it's a grid and orientation is not set, it's a next/previous
    // call, which is inherently horizontal. up/down will call next with
    // orientation set to vertical by default (see below on up/down
    // methods).
    isGrid ? orientation || "horizontal" : orientation);
    var canLoop = focusLoop && focusLoop !== oppositeOrientation;
    var canWrap = isGrid && focusWrap && focusWrap !== oppositeOrientation; // previous and up methods will set hasNullItem, but when calling next
    // directly, hasNullItem will only be true if if it's not a grid and
    // focusLoop is set to true, which means that pressing right or down keys
    // on grids will never focus the composite container element. On
    // one-dimensional composites that don't loop, pressing right or down
    // keys also doesn't focus on the composite container element.

    hasNullItem = hasNullItem || !isGrid && canLoop && includesBaseElement;

    if (canLoop) {
      var loopItems = canWrap && !hasNullItem ? allItems : getItemsInRow(allItems, activeItem.rowId);
      var sortedItems = flipItems(loopItems, activeIdRef.current, hasNullItem);

      var _nextItem2 = findFirstEnabledItem(sortedItems, activeIdRef.current);

      return _nextItem2 == null ? void 0 : _nextItem2.id;
    }

    if (canWrap) {
      var _nextItem3 = findFirstEnabledItem( // We can use nextItems, which contains all the next items, including
      // items from other rows, to wrap between rows. However, if there is
      // a null item (the composite container), we'll only use the next
      // items in the row. So moving next from the last item will focus on
      // the composite container. On grid composites, horizontal navigation
      // never focuses on the composite container, only vertical.
      hasNullItem ? nextItemsInRow : nextItems, activeIdRef.current);

      var nextId = hasNullItem ? (_nextItem3 == null ? void 0 : _nextItem3.id) || null : _nextItem3 == null ? void 0 : _nextItem3.id;
      return nextId;
    }

    var nextItem = findFirstEnabledItem(nextItemsInRow, activeIdRef.current);

    if (!nextItem && hasNullItem) {
      return null;
    }

    return nextItem == null ? void 0 : nextItem.id;
  }, [focusLoop, focusWrap, includesBaseElement]);
  var next = useCallback(function (skip) {
    return getNextId(collection.items, orientation, false, skip);
  }, [getNextId, collection.items, orientation]);
  var previous = useCallback(function (skip) {
    var _findFirstEnabledItem3; // If activeId is initially set to null or if includesBaseElement is set
    // to true, then the composite container will be focusable while
    // navigating with arrow keys. But, if it's a grid, we don't want to
    // focus on the composite container with horizontal navigation.


    var isGrid = !!((_findFirstEnabledItem3 = findFirstEnabledItem(collection.items)) != null && _findFirstEnabledItem3.rowId);
    var hasNullItem = !isGrid && includesBaseElement;
    return getNextId(reverseArray(collection.items), orientation, hasNullItem, skip);
  }, [collection.items, getNextId, orientation, includesBaseElement]);
  var down = useCallback(function (skip) {
    var shouldShift = focusShift && !skip; // First, we make sure rows have the same number of items by filling it
    // with disabled fake items. Then, we reorganize the items.

    var verticalItems = verticalizeItems(flatten2DArray(normalizeRows(groupItemsByRows(collection.items), activeIdRef.current, shouldShift)));
    var canLoop = focusLoop && focusLoop !== "horizontal"; // Pressing down arrow key will only focus on the composite container if
    // loop is true, both, or vertical.

    var hasNullItem = canLoop && includesBaseElement;
    return getNextId(verticalItems, "vertical", hasNullItem, skip);
  }, [collection.items, getNextId, focusShift, focusLoop]);
  var up = useCallback(function (skip) {
    var shouldShift = focusShift && !skip;
    var verticalItems = verticalizeItems(reverseArray(flatten2DArray(normalizeRows(groupItemsByRows(collection.items), activeIdRef.current, shouldShift)))); // If activeId is initially set to null, we'll always focus on the
    // composite container when the up arrow key is pressed in the first row.

    var hasNullItem = includesBaseElement;
    return getNextId(verticalItems, "vertical", hasNullItem, skip);
  }, [collection.items, getNextId, focusShift]);
  var state = useMemo(function () {
    return _objectSpread(_objectSpread({}, collection), {}, {
      baseRef: baseRef,
      orientation: orientation,
      rtl: rtl,
      virtualFocus: virtualFocus,
      focusLoop: focusLoop,
      focusWrap: focusWrap,
      focusShift: focusShift,
      moves: moves,
      setMoves: setMoves,
      includesBaseElement: includesBaseElement,
      activeId: activeId,
      setActiveId: setActiveId,
      move: move,
      next: next,
      previous: previous,
      up: up,
      down: down,
      first: first,
      last: last
    });
  }, [collection, baseRef, orientation, rtl, virtualFocus, focusLoop, focusWrap, focusShift, moves, setMoves, includesBaseElement, activeId, setActiveId, move, next, previous, up, down, first, last]);
  return useStorePublisher(state);
}

export { useCompositeState };